/**
 * Every class of failure that stops a user's work, and where each one
 * reports `user_blocking_failure` (#6012, P0 alert path 2026-10-25). The
 * PostHog destination "User-blocking failures → Discord #alerting" accepts
 * exactly these surfaces (`scripts/cto/lib/user-blocking-alert-destination.mjs`
 * keeps the same list), and `tests/user-blocking-alert-coverage.test.ts`
 * fails when a class has no emitter or a surface is missing from the filter.
 * Shared by the browser reporter and the Convex server reporter, so keep it
 * free of imports.
 */
export const USER_BLOCKING_FAILURE_EVENT = 'user_blocking_failure';

export const USER_BLOCKING_SURFACES = [
  'send',
  'create_thread',
  'upload',
  'page_crash',
  /** Listen / read-aloud could not produce or play audio. */
  'voice',
  /** A General Agent tool that failed in a way the agent cannot fix. */
  'tool',
  /** A queued generation or file job whose output failed. */
  'job',
  /** A reply that never reached the user's WhatsApp or iMessage chat. */
  'delivery',
] as const;

export type UserBlockingSurface = (typeof USER_BLOCKING_SURFACES)[number];

/** FNV-1a over the message with numbers and ids blanked; 8 hex chars. */
export function userBlockingErrorFingerprint(message: string): string {
  const normalized = message
    .replace(/\[Request ID:[^\]]*\]/g, '')
    .replace(/[A-Za-z0-9_-]*\d[A-Za-z0-9_-]*/g, '#')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
  let hash = 0x811c9dc5;
  for (let index = 0; index < normalized.length; index += 1) {
    hash ^= normalized.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash.toString(16).padStart(8, '0');
}

/** Surfaces the Convex server reports; the rest come from the browser. */
export const SERVER_USER_BLOCKING_SURFACES = [
  'tool',
  'job',
  'delivery',
] as const satisfies readonly UserBlockingSurface[];

export type ServerUserBlockingSurface =
  (typeof SERVER_USER_BLOCKING_SURFACES)[number];

export interface UserBlockingFailureClass {
  id: string;
  surface: UserBlockingSurface;
  /** The function that sends the event; its file names the surface. */
  reporter: { file: string; name: string };
  /** Repo-relative files where this failure happens; each calls `reporter`. */
  callers: readonly string[];
}

const BROWSER_REPORTER = {
  file: 'src/lib/observability/user-blocking-failure-reporter.ts',
  name: 'reportUserBlockingFailure',
};

/**
 * The audit table (`docs/operations/agent-playbook/error-watch.md`) in code.
 * A new user-facing failure path adds its row here together with its
 * reporter call.
 */
export const USER_BLOCKING_FAILURE_CLASSES: readonly UserBlockingFailureClass[] =
  [
    {
      id: 'org-chat-send',
      surface: 'send',
      reporter: BROWSER_REPORTER,
      callers: [
        'src/components/org-chat/interface/use-composer-handle-send.tsx',
        'src/components/org-chat/use-org-thread-steer-mutation.ts',
        'src/components/org-chat/use-org-thread-queue-mutations.ts',
        'src/components/your-imai/use-your-imai-conversation.ts',
      ],
    },
    {
      id: 'new-task',
      surface: 'create_thread',
      reporter: {
        file: 'src/lib/observability/user-blocking-failure-reporter.ts',
        name: 'withUserBlockingFailureReport',
      },
      callers: ['src/components/home/design-composer/navigation-delivery.ts'],
    },
    {
      id: 'uploads',
      surface: 'upload',
      reporter: BROWSER_REPORTER,
      callers: [
        'src/components/org-chat/composer-upload-lifecycle.ts',
        'src/components/org-chat/artifact-thread-panel/workspace-directory-upload-alert.ts',
      ],
    },
    {
      id: 'page-crash',
      surface: 'page_crash',
      reporter: {
        file: 'src/lib/observability/user-blocking-failure-reporter.ts',
        name: 'reportPageCrash',
      },
      callers: ['src/app/error.tsx', 'src/components/error-boundary.tsx'],
    },
    {
      id: 'listen-button',
      surface: 'voice',
      reporter: {
        file: 'src/components/chat/narration-failure-report.ts',
        name: 'reportNarrationFailure',
      },
      callers: ['src/components/chat/assistant-response-narration.tsx'],
    },
    {
      id: 'ga-tool-failure',
      surface: 'tool',
      reporter: {
        file: 'convex/products/ai/tools/workspace_bash_failure_observability.helpers.ts',
        name: 'captureWorkspaceBashFailure',
      },
      callers: [
        'convex/products/ai/tools/workspace_bash_execute_workspace_bash_command.helpers.ts',
        'convex/products/ai/tools/workspace_bash_run_workspace_bash_for_org.helpers.ts',
        'convex/products/ai/tools/workspace_bash_workspace_bash.helpers.ts',
      ],
    },
    {
      id: 'job-output-failure',
      surface: 'job',
      reporter: {
        file: 'convex/generations/workspace_image_failure_telemetry.helpers.ts',
        name: 'reportWorkspaceJobOutputFailure',
      },
      callers: [
        'convex/generations/mutation_fail_workspace_file_job_output_in_transaction.ts',
        'convex/generations/mutation_run_stale_workspace_generation_watchdog.ts',
      ],
    },
    {
      id: 'job-failure-without-output',
      surface: 'job',
      reporter: {
        file: 'convex/generations/workspace_image_failure_telemetry.helpers.ts',
        name: 'reportWorkspaceJobFailure',
      },
      callers: [
        'convex/generations/mutation_fail_workspace_file_job_output_in_transaction.ts',
      ],
    },
    {
      id: 'job-stuck',
      surface: 'job',
      reporter: {
        file: 'convex/generations/stuck_job_alert.helpers.ts',
        name: 'reportStuckJobGroups',
      },
      callers: [
        'convex/generations/mutation_run_stale_workspace_generation_watchdog.ts',
      ],
      // Cron: convex/cron_registrations/generation_recovery.ts (every 10 min).
    },
    {
      id: 'image-job-failure',
      surface: 'job',
      reporter: {
        file: 'convex/generations/workspace_image_failure_telemetry.helpers.ts',
        name: 'reportWorkspaceImageFailure',
      },
      callers: [
        'convex/generations/mutation_fail_image_generation_job.ts',
        'convex/generations/mutation_handle_workspace_image_generation_complete.ts',
      ],
    },
    {
      id: 'channel-delivery',
      surface: 'delivery',
      reporter: {
        file: 'convex/messaging/delivery_payload.helpers.ts',
        name: 'reportDeliveryUserBlockingFailure',
      },
      callers: [
        'convex/messaging/delivery.ts',
        'convex/messaging/delivery_payload.helpers.ts',
        'convex/messaging/cleanup.helpers.ts',
      ],
    },
  ];
